import React, { useEffect, useState } from "react";
import { MessageSquare, ThumbsUp, Send, Trash2, Edit, Check, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import SEO from "../components/SEO";
import {
  getQuestions,
  addQuestion,
  addComment,
  likeQuestion,
  deletePostAPI,
  deleteCommentAPI,
  editPostAPI
} from "../services/forumService";

const Forum = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [commentInputs, setCommentInputs] = useState({});
  const [editingPost, setEditingPost] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const res = await getQuestions();
      setPosts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load posts", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;

    try {
      const res = await addQuestion({ user_name: user?.name || "Guest", question: newPost });
      setPosts((prev) => [res.data, ...prev]);
      setNewPost("");
    } catch (err) {
      console.error("Failed to add post:", err);
    }
  };

  const handleComment = async (postId) => {
    const text = commentInputs[postId];
    if (!text) return;

    try {
      const res = await addComment({ question_id: postId, user_name: user?.name || "Guest", comment: text });
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, comments: [...p.comments, res.data] } : p))
      );
      setCommentInputs({ ...commentInputs, [postId]: "" });
    } catch (err) {
      console.error("Failed to add comment:", err);
    }
  };

  const handleLike = async (postId) => {
    setPosts((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, likes: (p.likes || 0) + 1 } : p))
    );
    try {
      await likeQuestion({ question_id: postId, user_name: user?.name || "Guest" });
      loadPosts();
    } catch (err) {
      console.error("Failed to like post:", err);
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, likes: Math.max((p.likes || 1) - 1, 0) } : p))
      );
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await deletePostAPI(postId);
      // Remove from state only
      setPosts(prev => prev.filter(p => p.id !== postId));
      // No loadPosts() here
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await deleteCommentAPI(commentId);
      setPosts(prev =>
        prev.map(p =>
          p.id === postId
            ? { ...p, comments: p.comments.filter(c => c.id !== commentId) }
            : p
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  const startEditing = (postId, text) => setEditingPost({ id: postId, text });
  const cancelEditing = () => setEditingPost(null);
  const saveEditing = async () => {
    const { id, text } = editingPost;
    setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, question: text } : p)));
    setEditingPost(null);

    try {
      await editPostAPI(id, text);
      loadPosts();
    } catch (err) {
      console.error("Failed to edit post:", err);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-12">
      <SEO 
        title="Community Forum" 
        description="Join the community of devotees. Ask questions, share your pilgrimage experiences, and connect." 
        keywords="devotee forum, hindu community, Nathnagri discussions" 
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h1 className="text-3xl font-bold text-stone-900 mb-3">सामुदायिक मंच</h1>
          <p className="text-stone-600">अन्य भक्तों से जुड़ें, प्रश्न पूछें और अपने अनुभव साझा करें।</p>
        </div>

        {/* Create Post */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-8">
          <form onSubmit={handlePost}>
            <textarea
              className="w-full bg-stone-50 text-stone-800 border-0 rounded-xl p-4 focus:ring-2 focus:ring-saffron-500 resize-none"
              rows="3"
              placeholder="कोई प्रश्न पूछें या कुछ साझा करें..."
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
            />
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center px-6 py-2.5 bg-saffron-600 text-white rounded-lg hover:bg-saffron-700"
              >
                <Send className="w-4 h-4 mr-2" /> पोस्ट करें
              </button>
            </div>
          </form>
        </div>

        {loading ? (
          <p className="text-center text-gray-500">Loading posts...</p>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-2xl p-6 shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-saffron-500 rounded-full flex items-center justify-center text-white font-bold">
                      {post.user_name?.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-semibold">{post.user_name}</h4>
                      <span className="text-gray-400 text-xs">
                        {post.created_at ? new Date(post.created_at).toLocaleString() : ""}
                      </span>
                    </div>
                  </div>

                  {user?.role === "admin" && (
                    <div className="flex gap-2">
                      <button onClick={() => startEditing(post.id, post.question)} className="text-blue-500 flex items-center gap-1">
                        <Edit className="w-4 h-4" /> Edit
                      </button>
                      <button onClick={() => handleDeletePost(post.id)} className="text-red-500 flex items-center gap-1">
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  )}
                </div>

                {editingPost?.id === post.id ? (
                  <div className="mb-4 flex flex-col gap-2">
                    <textarea
                      className="w-full border p-2 rounded"
                      value={editingPost.text}
                      onChange={(e) => setEditingPost({ ...editingPost, text: e.target.value })}
                    />
                    <div className="flex gap-2">
                      <button onClick={saveEditing} className="bg-green-500 text-white px-3 rounded flex items-center gap-1">
                        <Check className="w-4 h-4" /> Save
                      </button>
                      <button onClick={cancelEditing} className="bg-gray-300 text-black px-3 rounded flex items-center gap-1">
                        <X className="w-4 h-4" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mb-4">{post.question}</p>
                )}

                <div className="flex space-x-6 text-sm text-gray-600 mb-3">
                  <button onClick={() => handleLike(post.id)} className="flex items-center space-x-1">
                    <ThumbsUp className="w-4 h-4" />
                    <span>{post.likes || 0}</span>
                  </button>
                  <div className="flex items-center space-x-1">
                    <MessageSquare className="w-4 h-4" />
                    <span>{post.comments?.length || 0}</span>
                  </div>
                </div>

                {/* Comments */}
                <div className="mt-2 space-y-2">
                  {post.comments?.map((c) => (
                    <div key={c.id} className="bg-gray-100 p-2 rounded flex justify-between items-center">
                      <span>
                        <span className="text-gray-500 text-xs mr-2">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</span>
                        <strong>{c.user_name}:</strong> {c.comment}
                      </span>
                      {user?.role === "admin" && (
                        <button onClick={() => handleDeleteComment(post.id, c.id)} className="text-red-500 text-sm hover:underline flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex gap-2">
                  <input
                    className="flex-1 border p-2 rounded"
                    placeholder="Comment..."
                    value={commentInputs[post.id] || ""}
                    onChange={(e) => setCommentInputs({ ...commentInputs, [post.id]: e.target.value })}
                  />
                  <button onClick={() => handleComment(post.id)} className="bg-saffron-600 text-white px-3 rounded">
                    Send
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Forum;